import React, { useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import SearchSelect from "@ui-components/Select/GenericSelect";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { isCarrierAccessEnabled } from "@shared/config/featureFlags";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import { DetailLink } from "../../Shared/DetailLink";
import { legStatusTag } from "../../CarrierAccess/CarrierAccessDetails/carrierAccessLegs";
import type { CarrierAccessLeg } from "../../CarrierAccess/carrierAccess.types";
import {
  carrierMediumLabel,
  priorityLabel,
} from "kontrolEdge/helpers/carrierAccess";
import {
  addExtenderPopCarrierAccessLegAPI,
  removeExtenderPopCarrierAccessLegAPI,
  updateExtenderPopLegAPI,
  type LegCaptureBody,
} from "../requests/request";
import {
  apiErrorMessage,
  carrierAccessDetailPath,
  groupSiteCarrierAccess,
  legSourceLabel,
  type SiteLeg,
} from "../extenderPopCarrierAccessData";
import DeclareCarrierAccessModal from "./DeclareCarrierAccessModal";
import { LegCaptureSelect } from "./LegCaptureSelect";
import AddManualLegModal from "./AddManualLegModal";

type Leg = SiteLeg & { status?: CarrierAccessLeg["status"]; statusMessage?: string | null };

interface ExtenderPopCarrierAccessProps {
  extenderPopId: number;
  tenantId: number;
  siteName: string | null;
  cpes: { id: number; serviceId: string; name: string | null }[];
  legs: Leg[];
  /**
   * The Admin Center's view (CTX-830): declare the Backhaul, change its
   * legs and capture each leg's medium and priority. Off elsewhere, which
   * only reads.
   */
  canManage?: boolean;
  /** Reload the site after a write. */
  onChanged?: () => void;
}

/**
 * The Backhaul (Carrier Access) of the CPE site (CTX-830): every leg of its
 * routers, with its medium (CTX-800) and priority, the sold service (84-) it
 * backs, how it got here and its status. The Admin Center renders it with
 * `canManage` for a Global Admin.
 */
export default function ExtenderPopCarrierAccess({
  extenderPopId,
  tenantId,
  siteName,
  cpes,
  legs,
  canManage = false,
  onChanged = () => undefined,
}: ExtenderPopCarrierAccessProps) {
  const api = useAxiosAuth0();
  const [modal, setModal] = useState<"declare" | "manual" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { accesses, freeLegs } = groupSiteCarrierAccess(legs);
  const cpeOf = new Map(cpes.map((cpe) => [cpe.id, cpe.serviceId]));

  const run = async (request: () => Promise<unknown>, fallback: string) => {
    setError(null);
    try {
      await request();
      onChanged();
    } catch (requestError) {
      setError(apiErrorMessage(requestError, fallback));
    }
  };

  const link = (leg: Leg, carrierAccessId: number) =>
    run(
      () =>
        addExtenderPopCarrierAccessLegAPI(api, extenderPopId, carrierAccessId, tenantId, {
          legId: leg.id,
        }),
      "Could not link the leg"
    );

  const capture = (leg: Leg, body: LegCaptureBody) =>
    run(
      () => updateExtenderPopLegAPI(api, extenderPopId, leg.id, tenantId, body),
      "Could not save the change"
    );

  const remove = (leg: Leg) => {
    if (!leg.carrierAccess) return;
    const what = leg.source === "manual" ? "Delete this manual leg" : "Release this leg";
    if (!window.confirm(`${what} from ${leg.carrierAccess.serviceId}?`)) return;
    const carrierAccessId = leg.carrierAccess.id;
    run(
      () =>
        removeExtenderPopCarrierAccessLegAPI(
          api,
          extenderPopId,
          carrierAccessId,
          leg.id,
          tenantId
        ),
      "Could not remove the leg"
    );
  };

  const columns: GenericTableColumn<Leg>[] = [
    { header: "Carrier", renderCell: (leg) => <div>{leg.name}</div> },
    { header: "Device", renderCell: (leg) => <div>{cpeOf.get(leg.cpeId) ?? "-"}</div> },
    { header: "Circuit", renderCell: (leg) => <div>{leg.circuitId ?? "-"}</div> },
    {
      header: "Medium",
      renderCell: (leg) =>
        canManage ? (
          <LegCaptureSelect
            field="medium"
            legKey={leg.id}
            value={leg.medium ?? null}
            onChange={(medium) => capture(leg, { medium })}
            emptyLabel={carrierMediumLabel(null)}
          />
        ) : (
          <div>{carrierMediumLabel(leg.medium)}</div>
        ),
      sortAccessor: (leg) => carrierMediumLabel(leg.medium),
    },
    {
      header: "Priority",
      renderCell: (leg) =>
        canManage ? (
          <LegCaptureSelect
            field="priority"
            legKey={leg.id}
            value={leg.priority ?? null}
            onChange={(priority) => capture(leg, { priority })}
            emptyLabel="Not set"
          />
        ) : (
          <div>{priorityLabel(leg.priority) ?? "Not set"}</div>
        ),
      sortAccessor: (leg) => priorityLabel(leg.priority) ?? "~",
    },
    {
      header: "Source",
      renderCell: (leg) => (
        <div className="flex items-center gap-1">
          {legSourceLabel(leg.source)}
          {!leg.observed && (
            <GenericTag variant="warning" label="Not seen" size="small" />
          )}
        </div>
      ),
    },
    {
      header: "Status",
      renderCell: (leg) => {
        const { label, variant } = legStatusTag(leg.status ?? null);
        return (
          <ServiceStatusValue
            label={label}
            variant={variant}
            tooltip={leg.statusMessage ?? undefined}
          />
        );
      },
    },
    {
      header: "Backhaul",
      renderCell: (leg) => {
        if (leg.carrierAccess) {
          return isCarrierAccessEnabled() ? (
            <DetailLink
              to={carrierAccessDetailPath(leg.carrierAccess.id)}
              label={leg.carrierAccess.serviceId}
            />
          ) : (
            <div>{leg.carrierAccess.serviceId}</div>
          );
        }
        if (!canManage || accesses.length === 0) return <div>-</div>;
        return (
          <SearchSelect
            placeholder="Link to…"
            options={accesses.map((access) => ({
              value: String(access.id),
              label: access.serviceId,
            }))}
            onSelect={(selected) => link(leg, Number(selected))}
          />
        );
      },
    },
    ...(canManage
      ? [
          {
            header: "",
            renderCell: (leg: Leg) =>
              leg.carrierAccess ? (
                <GenericButton
                  variant="danger"
                  size="sm"
                  label="Remove"
                  onClick={() => remove(leg)}
                />
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <>
      <GenericCard
        headerContent="Backhaul"
        isCollapsible={false}
        rightContent={
          canManage ? (
            <div className="flex gap-2">
              {accesses.length > 0 && (
                <GenericButton
                  variant="secondary"
                  size="sm"
                  label="Add leg by hand"
                  onClick={() => setModal("manual")}
                />
              )}
              <GenericButton
                size="sm"
                label="Declare Backhaul"
                onClick={() => setModal("declare")}
              />
            </div>
          ) : undefined
        }
        bodyContent={
          <div className="flex flex-col gap-2">
            {error && <p className="text-sm text-semantic-text-error">{error}</p>}
            <GenericTable
              columns={columns}
              data={legs}
              initialRowsPerPage={5}
              rowsPerPageOptions={[5, 10, 20]}
              width="100%"
              tableClassName="bg-semantic-bg-primary"
            />
          </div>
        }
      />
      {modal === "declare" && (
        <DeclareCarrierAccessModal
          isOpen
          onClose={() => setModal(null)}
          onDeclared={() => {
            setModal(null);
            onChanged();
          }}
          extenderPopId={extenderPopId}
          tenantId={tenantId}
          siteName={siteName}
          cpes={cpes}
          freeLegs={freeLegs}
        />
      )}
      {modal === "manual" && (
        <AddManualLegModal
          isOpen
          onClose={() => setModal(null)}
          onAdded={() => {
            setModal(null);
            onChanged();
          }}
          extenderPopId={extenderPopId}
          tenantId={tenantId}
          accesses={accesses}
          cpes={cpes}
        />
      )}
    </>
  );
}
